
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Layout from '@kronoss/bricks/Layout/Layout';
import { Container } from '@mui/material';
import Image from 'next/image';
import Toast from '@kronoss/components/Toast/Toast';
import { useForm } from 'react-hook-form';
import Trans from 'next-translate/Trans';
import { API_URL } from '@kronoss/utils/contants';
import axios from 'axios';
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import styles from '@kronoss/styles/pages/contact.module.scss';
import Grid from '@mui/material/Grid2';
import Button from '@kronoss/components/Button/Button';
import FormInput from '@kronoss/components/FormInput/FormInput';
import FormTextarea from '@kronoss/components/FormTextarea/FormTextarea';
import Head from 'next/head';
export type ContactUsFormValues = {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    comment?: string;
};
const ContactPage = () => {
    const { t } = useTranslation('contact');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { register, handleSubmit, reset, formState: { errors }, } = useForm<ContactUsFormValues>();
    const contactUs = handleSubmit(async (data) => {
        setIsProcessing(true);
        try {
            if (!executeRecaptcha) {
                console.log('reCAPTCHA not loaded');
                return;
            }
            const recaptchaToken = await executeRecaptcha('contact');
            await axios.post(`${API_URL}api/v1/contact/?recaptcha_token=${recaptchaToken}`, {
                email: data.email,
                phone: data.phone,
                first_name: data.firstName,
                last_name: data.lastName,
                comment: data.comment,
            });
            Toast({
                type: 'success',
                label: t('form.notifications.success'),
                message: (<div>
            <Trans i18nKey="contact:form.notifications.sent" components={{ br: <br /> }}/>
          </div>),
            });
            reset();
        }
        catch (error: any) {
            Toast({
                type: 'error',
                label: t('form.notifications.error'),
                message: error?.response?.data?.message || t('form.notifications.notSent'),
            });
        }
        finally {
            setIsProcessing(false);
        }
    });
    const validatePhoneNumber = (value: string | undefined) => {
        if (!value || value.trim() === '') {
            return '';
        }
        try {
            let phoneNumber;
            if (value[0] !== '+') {
                phoneNumber = parsePhoneNumberWithError('+' + value);
            }
            else {
                phoneNumber = parsePhoneNumberWithError(value);
            }
            // Check if the number is valid
            if (!phoneNumber.isValid()) {
                return t('form.phone.notValid');
            }
            return true; // Valid phone number
        }
        catch (error: any) {
            // Handle specific error messages
            switch (error.message) {
                case 'NOT_A_NUMBER':
                    return t('form.phone.notNumber');
                case 'INVALID_COUNTRY':
                    return t('form.phone.invalidCountry');
                case 'TOO_SHORT':
                    return t('form.phone.short');
                case 'TOO_LONG':
                    return t('form.phone.long');
                default:
                    return t('form.phone.invalid');
            }
        }
    };
    return (<>
      <Head>
        <title>{t('metainfo:contact.title')}</title>
        <link rel="canonical" href={`https://kronoss.education/contact`}/>
        <link rel="alternate" hrefLang="en" href={`https://kronoss.education/en/contact`}/>
        <link rel="alternate" hrefLang="es" href={`https://kronoss.education/es/contact`}/>
        <meta name="description" content={t('metainfo:contact.slogan')}/>
        {/* OG Tags */}
        <meta property="og:title" content={t('metainfo:contact.slogan')}/>
        <meta property="og:description" content={t('metainfo:contact.description')}/>
        {/*<meta*/}
        {/*  property="og:image"*/}
        {/*  content={`${CLOUDFRONT_URL}ContactBanner.webp`}*/}
        {/*/>*/}

        {/* Twitter Tags */}
        <meta name="twitter:title" content={t('metainfo:contact.slogan')}/>
        <meta name="twitter:description" content={t('metainfo:contact.description')}/>
        {/*<meta*/}
        {/*  name="twitter:image"*/}
        {/*  content={`${CLOUDFRONT_URL}HomeBanner.webp`}*/}
        {/*/>*/}
        {/*<meta name="twitter:image:alt" content={t('contact.slogan')} />*/}
      </Head>
      <Layout>
        <Container>
          <div className={styles.ContactPage}>
            <Grid container spacing={8}>
              <Grid size={{ md: 6, xs: 12 }}>
                <div>
                  <Image src={'/images/contact-hero.png'} alt={'Contact us'} height={500} width={515} layout={'responsive'}/>
                </div>
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                <div>
                  <div className={styles.Title}>{t('title')}</div>
                  <div className={styles.Subtitle}>{t('subtitle')}</div>
                  <form className={styles.ContactForm} onSubmit={contactUs}>
                    <Grid container spacing={2}>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <FormInput label={t('form.firstName.label')} placeholder={t('form.firstName.placeholder')} {...register('firstName', {
        required: true,
        pattern: {
            value: /^[A-Za-z\s]+$/,
            message: t('form.firstName.lettersAllowed'),
        },
        minLength: {
            value: 2,
            message: t('form.firstName.minLength', {
                count: 2,
            }),
        },
        maxLength: {
            value: 50,
            message: t('form.firstName.maxLength', {
                count: 50,
            }),
        },
    })} error={errors.firstName?.type === 'required'
            ? t('form.firstName.required')
            : errors.firstName?.message}/>
                      </Grid>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <FormInput label={t('form.lastName.label')} placeholder={t('form.lastName.placeholder')} {...register('lastName', {
        required: true,
        pattern: {
            value: /^[A-Za-z\s]+$/,
            message: t('form.lastName.lettersAllowed'),
        },
        minLength: {
            value: 2,
            message: t('form.lastName.minLength', {
                count: 2,
            }),
        },
        maxLength: {
            value: 50,
            message: t('form.lastName.maxLength', {
                count: 50,
            }),
        },
    })} error={errors.lastName?.type === 'required'
            ? t('form.lastName.required')
            : errors.lastName?.message}/>
                      </Grid>
                      <Grid size={12}>
                        <FormInput label={t('form.email.label')} placeholder={t('form.email.placeholder')} type="email" {...register('email', {
        pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: t('form.email.invalid'),
        },
        required: true,
    })} error={errors.email?.type === 'required'
            ? t('form.email.required')
            : errors.email?.message}/>
                      </Grid>
                      <Grid size={12}>
                        <FormInput label={t('form.phone.label')} placeholder={t('form.phone.placeholder')} type="tel" {...register('phone', {
        required: false,
        validate: validatePhoneNumber,
    })} error={errors.phone?.message}/>
                      </Grid>
                      <Grid size={12}>
                        <FormTextarea label={t('form.comment.label')} placeholder={t('form.comment.placeholder')} minRows={3} {...register('comment', {
        required: false,
        minLength: {
            value: 2,
            message: t('form.comment.minLength', {
                count: 2,
            }),
        },
        maxLength: {
            value: 500,
            message: t('form.comment.maxLength', {
                count: 500,
            }),
        },
    })} error={errors.comment?.message}/>
                      </Grid>
                      <Grid size={12} display="flex" justifyContent="flex-end" alignItems="center">
                        <Button type={'submit'} disabled={isProcessing} aria-label={t('form.submitButton.label')}>
                          {t('form.submitButton.label')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </Layout>
    </>);
};
export default ContactPage;

    async function __Next_Translate__getStaticProps__19c38bde8ab__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/contact',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19c38bde8ab__ as getStaticProps }
  